import styled, { useTheme } from 'styled-components';
import Modal from '../../../components/Modal';
import {
  BodyMedium,
  BodySmall,
  Heading4Text,
  ParagraphSmallText,
} from '../../../components/Texts';
import { PHONE_MEDIA_QUERY } from '../../../common/responsive';
import { useSelector, useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import { selectTokenByNetworkAndSymbol } from '../../../redux/slices/tokenSlice';
import Button from '../../../components/Button';
import {
  useDoublePromoGiftCodeMutation,
  useClaimPromoGiftCodeMutation,
} from '../../../redux/api/authorized/promotional';
import { toast } from 'react-toastify';
import SocialShareOptions from '../../../components/SocialShareOptions';
import {
  sendEvent,
  EVENT_PROMO_GIFTCARD_CLAIMED,
} from '../../../common/analytics';
import { openModal } from '../../../redux/slices/modals';
import { useLazyGetGiftQuery } from '../../../redux/api/authorized/gifts';
import { useSolana } from '../../../hooks/useSolana';
import GiftCard from '../../../components/GiftCard';
import { useGetCurrentUserProfileQuery } from '../../../redux/api/authorized/auth';
import { selectPrivyToken } from '../../../redux/slices/auth';

const ValueIncreasedContainer = styled.div`
  background-color: ${({ theme }) => theme.primary.warningLight7};
  border: 1px solid ${({ theme }) => theme.primary.warning};
  border-radius: 16px;
  padding: 12px 16px;
  min-width: 390px;
  display: flex;
  gap: 8px;
  @media ${PHONE_MEDIA_QUERY} {
    min-width: unset;
    width: 100%;
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

const Line = styled.div`
  width: 100%;
  border-bottom: 1px dashed ${({ theme }) => theme.border.dashed};
`;

const DoubleGiftModal = ({ onClose, promoGift, promoCode }) => {
  const [mode, setMode] = useState('claim');
  const theme = useTheme();
  const [timeLeft, setTimeLeft] = useState(60);
  const dispatch = useDispatch();

  const privyToken = useSelector(selectPrivyToken);

  const { data: userData } = useGetCurrentUserProfileQuery(undefined, {
    skip: !privyToken,
  });

  const [doublePromoGiftCode, { isLoading: isDoubling }] =
    useDoublePromoGiftCodeMutation();
  const [claimPromoGiftCode, { isLoading: isClaiming }] =
    useClaimPromoGiftCodeMutation();
  const [triggerGetGift] = useLazyGetGiftQuery();

  const { refreshTokenBalances } = useSolana();

  const tokenDetails = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', promoGift?.token),
  );

  useEffect(() => {
    const usersMultiplied = promoGift?.usersMultiplied;

    if (usersMultiplied?.includes(userData?.id)) {
      setMode('double');
    }
  }, [promoGift, userData]);

  // Countdown timer effect - only start when in 'claim' mode
  useEffect(() => {
    if (mode === 'claim' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(timer);
    } else if (mode === 'claim' && timeLeft === 0) {
      // Time's up - automatically claim the gift
      toast.info("Time's up! Automatically claiming your gift...");
      handleClaim();
    }
  }, [mode, timeLeft]);

  const handleDouble = async () => {
    try {
      await doublePromoGiftCode(promoCode).unwrap();
      setMode('double');
    } catch (error) {
      toast.error(error.message || 'Failed to double promo code');
    }
  };

  const handleClaim = async () => {
    try {
      const result = await claimPromoGiftCode(promoCode).unwrap();

      sendEvent(EVENT_PROMO_GIFTCARD_CLAIMED, {
        id: promoGift?.id,
        token: promoGift?.token,
        amount: promoGift?.amount,
      });

      // Fetch the full gift data if we have a giftId
      if (result) {
        const giftResult = await triggerGetGift(result?.gift?.id);
        if (giftResult?.data?.gift) {
          // Open the giftClaimed modal with the full gift data
          dispatch(
            openModal({
              name: 'giftClaimed',
              props: {
                gift: giftResult.data.gift,
              },
            }),
          );
          refreshTokenBalances();
        } else {
          // Show error if we couldn't fetch the gift data
          toast.error(
            'Gift claimed successfully but there was an error loading gift details. Please check your portfolio.',
          );
        }
      } else {
        // Show error if no giftId in the response
        toast.error(
          'Gift claimed successfully but there was an error. Please check your portfolio.',
        );
      }
    } catch (error) {
      toast.error(error.message || 'Failed to claim promo code');
    }
  };

  return (
    <Modal onClose={onClose}>
      <div
        style={{
          display: 'flex',
          gap: 20,
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: '100%',
        }}
      >
        <div style={{ display: 'flex', gap: 6, flexDirection: 'column' }}>
          <Heading4Text
            style={{ fontWeight: '600', marginTop: 20, textAlign: 'center' }}
          >
            Double or Drop?
          </Heading4Text>
          <BodyMedium
            style={{
              color: theme.text.regular,
              textAlign: 'center',
            }}
          >
            Send the gift to a friend to double the value or claim it for
            yourself.
          </BodyMedium>
        </div>
        <GiftCard
          cardImage={promoGift?.image}
          token={tokenDetails}
          tokenAmount={promoGift?.amount}
          editable={false}
        />
        <Line />
        {mode === 'double' && (
          <div
            style={{
              display: 'flex',
              gap: 30,
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <BodyMedium
                style={{
                  color: theme.text.regular,
                  textAlign: 'center',
                }}
              >
                Share the link with your friends to keep the gift chain going!
              </BodyMedium>
            </div>
            <SocialShareOptions
              claimUrl={`${window.location.origin}?gift=${promoCode}`}
            />
          </div>
        )}
        {mode === 'claim' && (
          <div
            style={{
              display: 'flex',
              gap: 30,
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <ValueIncreasedContainer>
              <div style={{ display: 'flex', gap: 4, flexDirection: 'column' }}>
                <ParagraphSmallText style={{ fontWeight: 600 }}>
                  Clock's Running
                </ParagraphSmallText>
                <BodySmall>
                  {timeLeft} seconds left - double it or lose the chance!
                </BodySmall>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 4,
                  marginLeft: 'auto',
                }}
              >
                <img src='/timer.svg' width={17} height={17} />
              </div>
            </ValueIncreasedContainer>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <BodyMedium
                style={{
                  color: theme.text.regular,
                  textAlign: 'center',
                }}
              >
                Don’t kill the chain — keep it alive 🚀
              </BodyMedium>
            </div>
            <ButtonContainer>
              <Button onClick={handleDouble} loading={isDoubling}>
                Double and Send
              </Button>
              <Button
                onClick={handleClaim}
                variant='secondary'
                loading={isClaiming}
              >
                Claim
              </Button>
            </ButtonContainer>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DoubleGiftModal;
